/** @format */
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	Component,
	Input,
	OnChanges,
	Inject,
	PLATFORM_ID,
	Renderer2,
	OnInit,
	Output,
	EventEmitter
} from '@angular/core';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ImageFormat, MediaSetItem } from '@shared/media/data-access';
import { IsBotService, WINDOW } from '@shared/utils/services';
import { ProductAnalyticsService } from '@shared/data-access/services';

const IMAGE_HOST = 'https://dks.scene7.com/is/image/';

@Component({
    selector: 'homefield-product-image',
    templateUrl: './product-image.component.html',
    styleUrls: ['./product-image.component.scss'],
    standalone: false
})
export class HomefieldProductImageComponent implements OnChanges, OnInit {
	constructor(
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) protected platformId: any,
		@Inject(WINDOW) protected window: Window,
		private analyticsService: ProductAnalyticsService,
		private isBotService: IsBotService,
		private renderer: Renderer2
	) {}

	@Input() defaultColor;
	@Input() ecode;
	@Input() height = '200';
	@Input() imageUrl: string;
	@Input() imgClasses: string;
	@Input() imgFormat: ImageFormat;
	@Input() isLCPImage = false;
	@Input() isNgOptimizedImageSupported = false;
	@Input() productName;
	@Input() selectedSwatchColor: string | undefined;
	@Input() sourceImg: MediaSetItem | string;
	@Input() width = '100';

	@Output() errorHandlerFunc: EventEmitter<any> = new EventEmitter();

	private imageColor = '';
	// Added the # placeholder because the NgOptimizeImage trigger the test error of the
	// ngSrc initialized with "falsy" value
	public imageSrc = '#';

	private static isLCPLoaded = false;

	ngOnChanges() {
		if (this.sourceImg) {
			this.imageSrc =
				typeof this.sourceImg === 'string'
					? this.sourceImg
					: `${this.sourceImg.templateSrc || this.sourceImg.poster}?qlt=70&wid=${
							this.width
					  }&fmt=${this.imgFormat || 'webp'}&op_sharpen=1`;
			return;
		}

		this.imageColor = this.selectedSwatchColor || this.defaultColor || this.imageColor;
		this.imageColor = this.imageColor?.replace(/[^\w]/gi, '_');

		let imagePath = IMAGE_HOST;
		if (
			isPlatformBrowser(this.platformId) &&
			window.location.hostname.includes(`dksxchange`) &&
			!window.location.hostname.includes(`prod-`)
		) {
			imagePath = `${imagePath}GolfGalaxyTest`;
		} else {
			imagePath = `${imagePath}dkscdn`;
		}

		if (this.imageUrl) {
			this.imageSrc = this.imageColor
				? this.imageUrl.replace(/_([^?]+)/, `_${this.imageColor}`)
				: this.imageUrl;
		} else {
			this.imageSrc = this.imageColor
				? `${imagePath}/${this.ecode}_${this.imageColor}_is/?wid=252&hei=252&qlt=85,0&fmt=jpg&op_sharpen=1`
				: `${imagePath}/${this.ecode}_is/?wid=252&hei=252&qlt=85,0&fmt=jpg&op_sharpen=1`;
		}
	}

	ngOnInit(): void {
		if (!HomefieldProductImageComponent.isLCPLoaded) {
			this.preloadImage(this.imageSrc);
		}
	}

	/**
	 * @description Posts to rumrunner when a missing product image is detected
	 */
	public handleImageError(requestedImg: string): void {
		if (
			isPlatformBrowser(this.platformId) &&
			!this.isBotService.isBot(window.navigator.userAgent)
		) {
			this.analyticsService.emitMissingImageEvent({
				pageUrl: window.location.href,
				requestedImg,
				ecode: this.ecode,
				productName: this.productName,
				color: this.imageColor
			});

			this.imageSrc = DCSG_CONSTANTS.imageUnavailableResized;
		}

		if (this.errorHandlerFunc) {
			this.errorHandlerFunc.emit();
		}
	}

	/**
	 * @description Creates a native DOM link element and sets necessary properties
	 *
	 * @param imageUrl image URL
	 */
	private preloadImage(imageUrl: string): void {
		const linkEl = this.renderer.createElement('link');
		this.renderer.setAttribute(linkEl, 'rel', 'preload');
		this.renderer.setAttribute(linkEl, 'as', 'image');
		this.renderer.setAttribute(linkEl, 'fetchpriority', 'high');
		this.renderer.setAttribute(linkEl, 'href', imageUrl);

		const headEl = this.document.head;
		this.renderer.insertBefore(headEl, linkEl, headEl.firstChild);

		HomefieldProductImageComponent.isLCPLoaded = true;
	}
}
